/**
 * Clients main view.
 */
/* global BackboneEx */
/* eslint-disable func-names, no-underscore-dangle */
import { enableRecording, trackEvent } from 'common/analytics';
import apiUrl from 'common/api-url';
import moment from 'common/moment';
import { L1Page, NavigationAnalytics } from 'common/tracking/navigation';
import Wahanda from 'common/wahanda';
import Cookie from 'js-cookie';
import downloadFile from 'services/downloadFile';
import { track } from 'src/tracking/trackingService';
import _ from 'underscore';

App.Views.Clients = BackboneEx.View.Main.extend({
  events: {
    'click tr.consumer': 'onClickConsumer',
    'click .a-new': 'onClickNewConsumer',
    'click .a-import': 'onImportStart',
    'click .a-mass-email': 'onMassEmail',
    'click .a-bulk-delete': 'onBulkDelete',
    'click .a-open-filter': '_showFilter',
    'click .filter .title .icon': '_closeFilter',
    'click .a-reset-filter': '_resetFilter',
    'click .a-reset-all': 'clickResetFilter',
    'click .no-data .link': 'clearFilter',
    'click .a-submit-form': 'submitForm',
    'click .filter-option label': 'onClickFilterOption',
    'click .client-list-actions .js-action': 'onActionClick',
    'click .js-export-client-list': 'clientExportClick',
  },

  limit: 200,
  includes: ['last-visit'],
  fetchSensitivity: 700,
  // How many times this search was "resumed" by fetching next result set. 0 means "first page".
  currentPage: 0,
  // Cearch results container
  $results: null,
  // Loading indicator
  $fetchIndicator: null,
  clientListEmptyState: null,
  clientListFooter: null,
  bookableOnTreatwellBanner: null,

  resultsHeight: null,
  searchHasMoreResults: false,
  searching: false,
  // XHR request for the last search
  searchRequest: null,
  // Was the last search executed with no search text, returning full client list?
  lastFetchedFullClientList: false,
  lastSearchOptions: null,
  activeFilters: {},
  filterOptions: {},
  scrollPositionToApply: null,
  // sorting options
  sortField: 'full-name',
  sortOrder: 'ASC',
  sortCookieId: 'clients:list-sort',
  defaultFilters: {},

  initialize() {
    enableRecording();

    NavigationAnalytics.trackPageView(L1Page.CLIENTS_LIST);

    const self = this;
    App.headerView.setModule('clients');
    this.$results = this.$('.client-list-body');

    this.collection = new App.Collections.Customers();
    this.collection.setFilters({
      limit: this.limit,
    });

    this.$results.scrollNotify({
      scroll(event, data) {
        self.onScrollChanged(data);
      },
    });

    $(window).resize(() => {
      self.updateDimensions();
      self.toggleTick();
    });

    this.$fetchIndicator = $(Wahanda.Template.renderTemplate('consumer-list-loading-more'));

    // Set sorting options
    this._setSortingFromCookie();
    this._setupListeners();
    this._setupFilters();
    this.setValidation();

    this.$('#client-search').searchTermChange({
      $clear: this.$('.clear-search'),
      search(event, text) {
        trackEvent('clients-list', 'update', 'search', text);

        self.onSearchFieldChange(text);
      },
      stop() {
        self.onSearchFieldCleared();
      },
    });
    this.$('.client-list-head').dynamicSorter({
      field: this.sortField,
      order: this.sortOrder,
      change(event, data) {
        trackEvent('clients-list', 'edit', 'order', data);

        self.onSortingChange(data);
      },
    });

    this.renderBookableOnTreatwellBanner();
  },

  _isReconnectFooterEnabled() {
    // The Reconnect footer renders the redesigned import modal directly, so it
    // must only take over once that redesign is also enabled — otherwise the
    // footer would have no working import flow.
    return (
      Wahanda.Features.isEnabled('CCM-1563-reconnect-client-list-footer') &&
      Wahanda.Features.isEnabled('CCM-1502-redesign-client-list-import')
    );
  },

  renderClientsExportButton: function renderClientsExportButton() {
    if (this._isReconnectFooterEnabled()) {
      return;
    }
    this.$('.js-export-client-list').wToggle(Wahanda.Permissions.canExportClientList());
  },

  // Renders the Reconnect footer into the actions bar and (re-)pushes the
  // current count / permission props. Re-invoked from renderResults whenever
  // the filter results change so the count and button visibility stay in sync.
  renderClientListFooter() {
    const node = this.$('.client-list-footer-reconnect').get(0);

    if (!node) {
      return;
    }

    this.$('.client-list-actions').addClass('has-reconnect-footer');
    this.$('.client-list-actions-legacy').addClass('hidden');
    this.$('.client-list-footer-reconnect').removeClass('hidden');

    if (!this.clientListFooter) {
      this.clientListFooter = App.ES6.Initializers.ClientListFooter({ node });
    }

    const canSendEmail = Boolean(
      this.totalCustomers &&
        this.filterResultCount !== 0 &&
        App.isFeatureSupported('mass-mailing') &&
        Wahanda.Permissions.editClientContactDetails() &&
        Wahanda.Permissions.viewClientContactDetails(),
    );

    this.clientListFooter.render({
      totalCustomers: this.totalCustomers || 0,
      filterResultCount: this.filterResultCount || 0,
      canSendEmail,
      canExport: Wahanda.Permissions.canExportClientList(),
      canCreateClients: Wahanda.Permissions.createClients(),
      canImportClients: Wahanda.Permissions.editClientContactDetails(),
      canBulkDelete: App.isMasquerading() && this.totalCustomers > 0,
      onSendEmail: () => this.onMassEmail(),
      onExport: () => this.clientExportClick(),
      // The footer renders the Add-new drawer and import modal itself; the only
      // hop left to Connect is launching the ClientsImport island (a Layer-2
      // Redux island, not a Reconnect component) from the modal's template branch.
      onUseTemplate: () =>
        App.ES6.Initializers.ClientsImport({
          refreshClientsList: () => this.refreshAfterAdd(),
          entryContext: {
            source: 'clients_list_import_method',
            autoOpened: false,
            clientListEmpty: this.totalCustomers === 0,
          },
        }).render(),
      onBulkDelete: () => this.onBulkDelete(),
      onResetFilters: () => this.clickResetFilter(),
    });
  },

  destroyClientListFooter() {
    if (!this.clientListFooter) {
      return;
    }

    this.clientListFooter.destroy();
    this.clientListFooter = null;
  },

  // Pure mount point — Reconnect's useBookableOnTreatwellBannerState owns show/hide
  // (backend eligibility CCM-1633 + localStorage dismissal), so this always
  // mounts and may render nothing.
  renderBookableOnTreatwellBanner() {
    const node = this.$('.bookable-on-treatwell-reconnect').get(0);

    if (!node) {
      return;
    }

    if (!this.bookableOnTreatwellBanner) {
      this.bookableOnTreatwellBanner = App.ES6.Initializers.BookableOnTreatwellBanner({
        node,
      });
    }

    this.bookableOnTreatwellBanner.render();
  },

  renderClientListEmptyState() {
    const node = this.$('.client-list-empty-state').get(0);

    if (!node) {
      return;
    }

    if (!this.clientListEmptyState) {
      this.clientListEmptyState = App.ES6.Initializers.ClientListEmptyState({
        node,
        onAddNew: () => this.onClickNewConsumerFromEmptyState(),
        onImport: () => this.onImportStartFromEmptyState(),
      });
    }

    this.clientListEmptyState.render({
      canCreateClients: Wahanda.Permissions.createClients(),
      canImportClients: Wahanda.Permissions.editClientContactDetails(),
    });
  },

  destroyClientListEmptyState() {
    if (!this.clientListEmptyState) {
      return;
    }

    this.clientListEmptyState.destroy();
    this.clientListEmptyState = null;
  },

  toggleClientListEmptyState(isVisible) {
    this.$('.client-list-empty-state').toggleClass('hidden', !isVisible);
    this.$('.clients-filters, .client-list').toggle(!isVisible);

    if (isVisible) {
      this.renderClientListEmptyState();
    } else {
      this.destroyClientListEmptyState();
    }
  },

  renderClientsAddButton: function renderClientsAddButton() {
    const canCreate = Wahanda.Permissions.createClients();
    const canImport = Wahanda.Permissions.editClientContactDetails();

    this.$('.js-action').wToggle(canCreate || canImport);
    this.$('.a-new').wToggle(canCreate);
    this.$('.a-import').wToggle(canImport);
  },

  setValidation() {
    const self = this;

    const validations = {
      submitHandler() {
        self._submitFilter();
      },
      invalidHandler() {
        const filterName = self.$('.filter:visible').data('name');
        trackEvent('clients-list', 'validation', filterName);
      },
      focusInvalid: true,
      ...Wahanda.Validate.Renderers.topTooltip,
    };

    this.$('form').each(function () {
      $(this).validate(validations);
    });
  },

  disableEmailButton() {
    const html = $('.email-button-tooltip').html();
    this.$('.a-mass-email').qtip({
      content: html,
      style: {
        classes: 'dialog-tooltip',
        width: '310px',
      },
      position: {
        my: 'bottom center',
        at: 'top center',
      },
      show: {
        event: false,
        ready: true,
      },
      hide: {
        event: 'click',
        fixed: true,
      },
    });
  },

  renderUpgradeOverlay() {
    this.$('.upgrade-overlay').show();
  },

  _setSortingFromCookie() {
    let cookie = Cookie.get(this.sortCookieId);
    if (cookie) {
      try {
        cookie = cookie.split(';');
        if (Array.isArray(cookie) && cookie.length === 2) {
          this.sortField = cookie[0];
          this.sortOrder = cookie[1];
        }
      } catch (e) {} // eslint-disable-line no-empty
    }
  },

  submitForm(event) {
    const $form = $(event.currentTarget).parents('form');
    $form.submit();
  },

  _setupListeners() {
    const E = Wahanda.Event;
    App.on(
      [E.CUSTOMER_SAVED, E.CUSTOMERS_IMPORT_SUCCESS, E.CUSTOMERS_BULK_DELETE].join(' '),
      () => {
        this.refreshAfterAdd();
      },
    )
      .on(E.CUSTOMER_DIALOG_OPENED, () => {
        this.$el.addClass('printable-hide');
      })
      .on(E.CUSTOMER_DIALOG_CLOSED, () => {
        this.$el.removeClass('printable-hide');
      })
      .on(E.CUSTOMER_ARCHIVED, (model) => {
        this.removeCustomerFromTable(model);
      });

    App.on(E.CALENDAR_APPOINTMENT_OPEN, ({ appointmentId }) => {
      App.Views.Forms.Appointment2.openById(appointmentId);
    });
  },
  _showFilter(event) {
    const $filter = $(event.currentTarget).siblings('.filter');
    this._closeFilter();
    $filter.show();
  },

  _getLastVisitFilterOptions(daysNumbers, filterOption) {
    const currentDate = Wahanda.Date.createVenueDate();
    const pastDate = Wahanda.Date.addDaysToDate(currentDate, daysNumbers[0] * -1);
    let lastVisitOptions = {};
    if (filterOption === 'more') {
      lastVisitOptions = {
        lastVisitDateTo: Wahanda.Date.toApiString(pastDate),
      };
    } else if (filterOption === 'exactly') {
      lastVisitOptions = {
        lastVisitDateFrom: Wahanda.Date.toApiString(pastDate),
        lastVisitDateTo: Wahanda.Date.toApiString(pastDate),
      };
    } else {
      lastVisitOptions = {
        lastVisitDateFrom: Wahanda.Date.toApiString(pastDate),
        lastVisitDateTo: Wahanda.Date.toApiString(currentDate),
      };
    }

    return lastVisitOptions;
  },

  _getOptinFilterOptions(filterOption) {
    return {
      sendMassEmails: filterOption,
    };
  },

  _getBookingCountFilterOptions(bookingCounts, filterOption) {
    let bookingCountOptions = {};
    if (filterOption === 'more') {
      bookingCountOptions = {
        bookingCountFrom: bookingCounts[0],
      };
    } else if (filterOption === 'exactly') {
      bookingCountOptions = {
        bookingCountFrom: bookingCounts[0],
        bookingCountTo: bookingCounts[1],
      };
    } else {
      bookingCountOptions = {
        bookingCountTo: bookingCounts[0],
      };
    }

    return bookingCountOptions;
  },

  removeOptInoptions() {
    const optInOptions = ['sendMassEmails'];
    optInOptions.forEach((optInOption) => delete this.filterOptions[optInOption]);
  },

  removeBookingCountOptions() {
    const bookingCountOptions = ['bookingCountFrom', 'bookingCountTo'];
    bookingCountOptions.forEach(
      (bookingCountOption) => delete this.filterOptions[bookingCountOption],
    );
  },

  removeLastVisitOptions() {
    const lastVisitOptions = ['lastVisitDateFrom', 'lastVisitDateTo'];
    lastVisitOptions.forEach((lastVisitOption) => delete this.filterOptions[lastVisitOption]);
  },

  _submitFilter() {
    const currentFilter = this.$('.filter:visible');
    const filterName = currentFilter.data('name');
    this._resetFilter(filterName, { reset: true });
    this.collection.reset();

    const filterOption = currentFilter.find('.filter-option input:checked').val();

    const filterValues = [];
    currentFilter.find('.filter-value input:visible').each(function (i) {
      filterValues[i] = $(this).val();
    });

    let filterOptions = {};

    switch (filterName) {
      case 'lastVisit':
        filterOptions = this._getLastVisitFilterOptions(filterValues, filterOption);
        trackEvent(
          'clients-list',
          'edit',
          'lastVisit-filter',
          Object.keys(filterOptions).join(', '),
        );
        break;
      case 'bookingCount':
        filterOptions = this._getBookingCountFilterOptions(filterValues, filterOption);
        trackEvent(
          'clients-list',
          'edit',
          'bookingCount-filter',
          Object.keys(filterOptions).join(', '),
        );
        break;
      case 'optIn':
        trackEvent('clients-list', 'edit', 'optIn-filter', filterOption);
        filterOptions = this._getOptinFilterOptions(filterOption);
        break;
      default:
        filterOptions = {};
    }
    Object.assign(this.filterOptions, filterOptions);

    this.collection.setFilters({
      searchText: $('#client-search').val(),
      ...this.filterOptions,
      ...this.getSorting(),
      limit: this.limit,
      include: this.includes,
    });

    this._initiateSearch();
    this._closeFilter();

    this.activeFilters[filterName] = true;
    App.trigger(Wahanda.Event.CONSUMER_LIST_FILTER_APPLIED, {
      filter: filterName,
    });
  },

  _resetFilter(filter, nosearch) {
    this.currentPage = 0;
    const filterName =
      filter !== 'lastVisit' || filter !== 'bookingCount'
        ? this.$('.filter:visible').data('name')
        : filter;
    this.collection.reset();

    switch (filterName) {
      case 'lastVisit':
        this.removeLastVisitOptions();
        break;
      case 'bookingCount':
        this.removeBookingCountOptions();
        break;
      case 'optIn':
        this.removeOptInoptions();
        break;
      default:
        this.filterOptions = {};
    }

    this.collection.setFilters({
      searchText: $('#client-search').val(),
      ...this.getSorting(),
      ...this.filterOptions,
      limit: this.limit,
      include: this.includes,
    });

    if (nosearch && nosearch.reset) {
      return;
    }

    this._initiateSearch();
    this._closeFilter();

    this.$('.filter .filter-value input').val(this.defaultFilters[filterName].defaultValue);
    this.$(
      `.filter .filter-option input[value="${this.defaultFilters[filterName].defaultOption}"]`,
    ).click();
    this.activeFilters[filterName] = false;
    App.trigger(Wahanda.Event.CONSUMER_LIST_FILTER_DROPPED, {
      filter: filterName,
    });
  },

  _initiateSearch() {
    const self = this;
    this.toggleClientListEmptyState(false);
    this.$results.loadmask();
    this.searching = true;
    this.searchRequest = this.collection.search({
      success(list, data) {
        self.renderResults(list, data);
      },
    });
  },

  _setupFilters() {
    const optInFilter = {
      filter: 'optIn',
      column: '.opt-in-column',
      className: 'a-filter-opt-in',
      text: {
        title: Wahanda.lang.clients.filters.optIn.title,
        filter: Wahanda.lang.clients.filters.optIn.filter,
        reset: Wahanda.lang.clients.filters.optIn.reset,
      },
      defaultValues: {
        defaultOption: 'true',
        defaultValue: '',
      },
      fieldName: 'opt-in',
      options: [
        {
          value: true,
          text: Wahanda.lang.clients.filters.optIn.true,
          checked: true,
        },
        {
          value: false,
          text: Wahanda.lang.clients.filters.optIn.false,
        },
      ],
    };

    const lastVisitFilter = {
      filter: 'lastVisit',
      className: 'a-filter-visit-date',
      text: Wahanda.lang.clients.filters.lastVisit,
      column: '.visit-column',
      fieldName: 'last-visit-date',
      input1: 'days',
      defaultValues: {
        defaultOption: 'less',
        defaultValue: '30',
      },
      options: [
        {
          value: 'less',
          text: Wahanda.lang.clients.filters.lastVisit.less,
          checked: true,
        },
        {
          value: 'exactly',
          text: Wahanda.lang.clients.filters.lastVisit.exactly,
        },
        {
          value: 'more',
          text: Wahanda.lang.clients.filters.lastVisit.more,
        },
      ],
    };
    const bookingCountFilter = {
      filter: 'bookingCount',
      className: 'a-filter-booking-count',
      text: Wahanda.lang.clients.filters.bookingCount,
      column: '.bookings-column',
      fieldName: 'booking-item-count',
      input1: 'bookings',
      input2: 'bookings2',
      range: true,
      defaultValues: {
        defaultOption: 'less',
        defaultValue: '',
      },
      options: [
        {
          value: 'less',
          text: Wahanda.lang.clients.filters.bookingCount.less,
          checked: true,
        },
        {
          value: 'exactly',
          text: Wahanda.lang.clients.filters.bookingCount.exactly,
        },
        {
          value: 'more',
          text: Wahanda.lang.clients.filters.bookingCount.more,
        },
      ],
    };
    // this._setupFilter(lastVisitFilter);
    this._setupFilter(bookingCountFilter);
    this._setupFilter(optInFilter);

    const $addButton = $('.js-action');
    $('body').on('click keydown', (event) => {
      const $openFilter = $('.filter:visible');
      const $filterIcon = $openFilter.siblings('.a-open-filter').find('.icon');
      if (event.type === 'click') {
        if (
          !$openFilter.is(event.target) &&
          !$filterIcon.is(event.target) &&
          $openFilter.has(event.target).length === 0
        ) {
          this._closeFilter();
        }
        if (
          !$addButton.is(event.target) &&
          !$addButton.is(event.target) &&
          $addButton.has(event.target).length === 0
        ) {
          this._closeTooltip();
        }
      } else if (event.type === 'keydown' && event.keyCode === 27) {
        this._closeFilter();
        this._closeTooltip();
      }
    });
  },

  _setupFilter(filter) {
    const filterName = filter.filter;
    const column = $('.client-list-head').find(filter.column);
    column.append(Wahanda.Template.renderTemplate('consumer-filter', filter));
    this.defaultFilters[filterName] = filter.defaultValues;
  },

  _closeFilter() {
    const $filters = $('.filter');
    $filters.hide();
  },

  render() {
    this.$('.client-hidden').toggle(true);

    const hasAccess = Wahanda.Permissions.viewClientList();
    this.$el.toggleClass('no-permission', !hasAccess);
    if (!hasAccess) {
      return;
    }

    this.search({
      searchText: '',
    });
    this.updateDimensions();
    this.renderClientsExportButton();
    this.renderClientsAddButton();
    if (this.shouldStartImport()) {
      const cleanUrl = new URL(window.location.href);
      const rawSource = cleanUrl.searchParams.get('source');
      this._pendingImportSource = rawSource === 'next_best_action' ? rawSource : null;
      cleanUrl.searchParams.delete('startImport');
      cleanUrl.searchParams.delete('source');
      window.history.replaceState({}, document.title, cleanUrl.toString());
      this._pendingAutoOpenImport = true;
    }
  },

  clientExportClick: function clientExportClick() {
    track('clients.management.client_list.export.clicked');

    const shouldShowDialog =
      Wahanda.Features.isEnabled('CCM-1044-client-export-insights-popup') &&
      !Wahanda.LocalStorage.getForVenue('export-reason-dialog-shown');

    if (shouldShowDialog) {
      App.ES6.Initializers.ExportReasonDialog({
        onExport: () => {
          Wahanda.LocalStorage.setForVenue('export-reason-dialog-shown', true);
          this.performClientExport();
        },
        onCancel: () => {},
      }).render();
    } else {
      this.performClientExport();
    }
  },

  performClientExport: function performClientExport() {
    const lang = Wahanda.lang.clients;
    const venueName = App.config.venueName();
    const todayDate = moment().formatDateDefault();

    const fileName = `${venueName} ${lang.exportsFileName} ${todayDate}.csv`;

    this.$el.loadmask();

    downloadFile({
      url: apiUrl('CLIENTS_EXPORT'),
      fileName,
      type: 'text/csv',
    }).finally(() => this.$el.unloadmask());

    track('clients.management.client_list.client_list_download.clicked');
  },

  onActionClick() {
    const $tooltip = $('.tooltip');
    $tooltip.show();
  },

  _closeTooltip() {
    const $tooltip = $('.tooltip');
    $tooltip.hide();
  },

  onClickFilterOption(event) {
    let $option;
    const $options = $('.filter-option label');
    if ($(event.target).parents('label').length > 0) {
      $option = $(event.target).parents('label');
    } else {
      $option = $(event.target);
    }
    const filter = $option.parents('.filter');
    if ($option.find('input').val() === 'exactly' && filter.find('.filters .values').length > 0) {
      filter.find('.filters .filter-value').hide();
      filter.find('.filters .filter-value.values').show();
    } else {
      filter.find('.filters .filter-value').show();
      filter.find('.filters .filter-value.values').hide();
    }

    $options.removeClass('selected');
    $option.addClass('selected');
  },

  search(options) {
    this.searching = true;
    this.currentPage = 0;
    this.$results.loadmask();
    // Clearing last content
    this._startScroller();

    this.lastFetchedFullClientList = !options.searchText;
    this.lastSearchOptions = options;

    this.collection.reset();
    this.collection.setFilters({
      ...options,
      ...this.lastSearchOptions,
      ...this.filterOptions,
      ...this.getSorting(),
      limit: this.limit,
      include: this.includes,
    });

    this._initiateSearch();
  },

  fetchMoreClients() {
    const self = this;
    this.searching = true;
    this.currentPage += 1;

    this.addFetchingMoreIndicator();

    this.searchRequest = this.collection.getSearchNextPage({
      success(list, data) {
        self.renderResults(list, data);
      },
    });
  },

  refreshAfterAdd() {
    if (this.lastFetchedFullClientList) {
      // No filtering applied
      if (this.currentPage === 0) {
        this.scrollPositionToApply = this.$results.scrollTop();
      }
      this.search(this.lastSearchOptions);
    } else {
      this.clearFilter();
    }
  },

  clickResetFilter() {
    trackEvent('clients-list', 'click', 'reset-all-filters');

    this.clearFilter();
  },

  /**
   * Clears user free-text search and refreshes ticons-filterhe list.
   */
  clearFilter() {
    this.$('#client-search').val('');
    this.filterOptions = {};
    this.activeFilters = {};
    this.search({});
  },

  addFetchingMoreIndicator() {
    this.$results.find('tbody').append(this.$fetchIndicator);
  },

  updateFilterIcons() {
    const filterOn = 'icons-filter-on';
    const filterOff = 'icons-filter';
    const $icons = $('.icon[data-filter]');
    $icons.removeClass(filterOn);
    $icons.addClass(filterOff);
    if (!this.activeFilters || Object.keys(this.activeFilters).length === 0) {
      return;
    }
    // eslint-disable-next-line no-undef
    _.each(this.activeFilters, (active, filter) => {
      if (active) {
        const $icon = $(`.icon[data-filter="${filter}"]`);
        $icon.addClass(filterOn);
        $icon.removeClass(filterOff);
      }
    });
  },

  isMultiLocale() {
    const channelSupportedLocales = App.config.getChannelSupportedLocales();

    if (!channelSupportedLocales) {
      return false;
    }

    const numberOfSupportedLocales = Object.keys(channelSupportedLocales).length;

    return numberOfSupportedLocales > 1;
  },

  renderResults(results, data) {
    const hasResults = results.length > 0;
    const $body = this.$results.find('tbody');
    const $resultCount = this.$('.client-list-count');
    const isFirstPage = this.currentPage === 0;
    const $massEmailButton = this.$('.mass-email-button');
    const $bulkDeleteButton = this.$('.bulk-delete-button');
    this.searchHasMoreResults = data.hasMoreResults;
    this.searching = false;
    if (isFirstPage) {
      this.totalCustomers = data.totalCustomers;
      this.filterResultCount = data.filterResultCount;
    }

    this.updateFilterIcons();
    if (isFirstPage) {
      // Clear previous results
      $body.empty();
    }

    if (!this.searchHasMoreResults) {
      this._stopScroller();
    }
    this.$('.client-list').toggleClass('empty', !hasResults && isFirstPage);
    this.toggleClientListEmptyState(isFirstPage && this.totalCustomers === 0);

    // Remove loading indicator
    this.$fetchIndicator.remove();

    const viewContacts = Wahanda.Permissions.viewClientContactDetails();

    this.$('.client-list-head').find('.b-contact').wToggle(viewContacts);
    this.$('.client-list-head').find('.b-multi-locale').wToggle(this.isMultiLocale());

    $body.append(
      Wahanda.Template.renderTemplate('consumer-list-items', {
        list: this._prepareConsumersForTemplate(results),
        withContacts: viewContacts,
        withLocale: this.isMultiLocale(),
      }),
    );

    this.bindTooltips();

    if (this._isReconnectFooterEnabled()) {
      this.renderClientListFooter();
    } else {
      $resultCount.html(
        Wahanda.Template.renderTemplate('consumer-list-result-count', {
          total: this.totalCustomers,
          filterCount: this.filterResultCount,
        }),
      );

      let showMassEmailButton;
      if (
        this.totalCustomers &&
        this.filterResultCount !== 0 &&
        App.isFeatureSupported('mass-mailing')
      ) {
        showMassEmailButton = true;
      }

      $massEmailButton.html(
        Wahanda.Template.renderTemplate('customer-mass-email-button-template', {
          resultsReturned: showMassEmailButton,
        }),
      );

      const showBulkDeleteButton = App.isMasquerading() && this.totalCustomers > 0;
      $bulkDeleteButton.html(
        Wahanda.Template.renderTemplate('customer-bulk-delete-button-template', {
          resultsReturned: showBulkDeleteButton,
        }),
      );
    }

    this.$results.unloadmask().shadows();
    this.toggleTick();

    if (this.scrollPositionToApply) {
      this.$results.scrollTop(this.scrollPositionToApply);
      this.scrollPositionToApply = null;
    }

    if (isFirstPage && this._pendingAutoOpenImport) {
      this._pendingAutoOpenImport = false;
      const entryContext = {
        source: this._pendingImportSource || 'direct',
        autoOpened: true,
        clientListEmpty: this.totalCustomers === 0,
      };
      this._pendingImportSource = null;
      this._startImport(entryContext);
    }

    this._triggerSearchResultEvent();
  },

  _prepareConsumersForTemplate(list) {
    const now = new Date();
    const result = [];
    for (let i = 0, len = list.length; i < len; i++) {
      const item = list[i].toJSON();
      if (item.locale) {
        let locale = Wahanda.lang.language[item.locale];
        if (!locale) {
          // eslint-disable-next-line no-undef
          const supportedLocales = _.invert(App.config.getChannelSupportedLocales());
          locale = supportedLocales[item.locale];
        }
        item.locale = locale;
      }

      if (item.lastVisit) {
        const visitDate = Wahanda.Date.createDate(item.lastVisit.visitDate);
        const dayDiff = Wahanda.Date.getDayDifference(now, visitDate);
        const visit = {
          date: Wahanda.Date.formatToDefaultDate(visitDate),
          daysAgo: this.getDistanceInWords(dayDiff),
        };
        item.visit = visit;

        delete item.lastVisit;
      }
      result.push(item);
    }
    return result;
  },

  getDistanceInWords(dayDiff) {
    if (dayDiff === 0) {
      return Wahanda.lang.date.today;
    }
    const template =
      Wahanda.lang.datetime.distance_in_words.x_days_ago[dayDiff === 1 ? 'one' : 'other'];
    return template.replace('{{count}}', dayDiff);
  },

  onScrollChanged(data) {
    if (!this.searchHasMoreResults || this.searching) {
      return;
    }
    const leftToScroll = data.maxScroll - (this.resultsHeight + data.scrollTop);
    if (this.fetchSensitivity > leftToScroll) {
      this.fetchMoreClients();
    }
  },

  updateDimensions() {
    this.resultsHeight = this.$results.height();
  },

  _stopScroller() {
    this.$results.scrollNotify('disable');
  },

  _startScroller() {
    this.$results.scrollNotify('enable');
  },

  openConsumerDetails({ id, bookingCount }) {
    // Invoke the initialiser
    App.ES6.Initializers.ClientOverview({
      onClose: () => {},
      onClientEdited: () => this.refreshAfterAdd(),
      clientId: id,
    }).render();
  },

  openConsumerEditForm(accessedFrom = 'clients.client_list') {
    App.ES6.Initializers.ClientAddOrEdit({
      onClose: ({ isSaved }) => {
        if (isSaved) {
          this.refreshAfterAdd();
        }
      },
      accessedFrom,
    }).render();
  },

  /**
   * Toggles a tick in the table head, if the list table is narrower than the header table.
   */
  toggleTick() {
    const $headTable = this.$('.client-list-head').find('table');
    const $listTable = this.$('.client-list-body').find('table');

    $headTable.toggleClass('has-tick', $headTable.width() > $listTable.width());
  },

  getSorting() {
    const sorting = {
      sortBy: this.sortField || 'full-name',
      sortOrder: this.sortOrder || 'ASC',
    };

    // Reverse order for last-visit-date
    if (sorting.sortBy === 'last-visit-date') {
      sorting.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    return sorting;
  },

  _triggerSearchResultEvent() {
    if (this.currentPage === 0) {
      // This is a genuine search event.
      App.trigger(Wahanda.Event.CONSUMER_LIST_SEARCHED, {
        filtered: !this.lastFetchedFullClientList,
      });
    } else {
      // This is a "next page loaded" event
      App.trigger(Wahanda.Event.CONSUMER_LIST_PAGINATED, {
        pageNumber: this.currentPage + 1,
      });
    }
  },

  getCurrentStateHash() {
    return this.getStateHash();
  },

  getStateHash() {
    return `venue/${App.getVenueId()}`;
  },

  removeCustomerFromTable(customerModel) {
    this.$('.client-list-body').find(`tr[data-id="${customerModel.id}"]`).remove();
  },

  // UI events

  onSearchFieldChange(text) {
    this._abortExistingSearch();
    this.search({ searchText: text });
  },

  onSearchFieldCleared() {
    this._abortExistingSearch();
    if (!this.lastFetchedFullClientList) {
      this.search({ searchText: null });
    }
  },

  _abortExistingSearch() {
    if (this.searchRequest) {
      this.searchRequest.abort();
      this.searchRequest = null;
    }
  },

  onSortingChange(data) {
    if (data.field === 'last-visit-date') {
      return;
    }

    Cookie.set(this.sortCookieId, [data.field, data.order].join(';'), {
      expires: 365,
    });

    this.sortField = data.field;
    this.sortOrder = data.order;

    this.search(this.lastSearchOptions);
  },

  onClickConsumer(event) {
    trackEvent('clients-list', 'click', 'client-details');

    const venueCustomerId = $(event.currentTarget).data('id');
    const bookingCount = $(event.currentTarget).data('booking-count');
    this.openConsumerDetails({
      id: venueCustomerId,
      bookingCount,
    });
  },

  onClickNewConsumer() {
    if (!Wahanda.Permissions.createClients()) {
      return;
    }

    trackEvent('clients-list', 'click', 'add-new-client');

    this.openConsumerEditForm();
  },

  onClickNewConsumerFromEmptyState() {
    if (!Wahanda.Permissions.createClients()) {
      return;
    }

    this.openConsumerEditForm('clients.client_list_empty_state');
  },

  useNewCustomerImportFlow() {
    return !window.location.search.includes('oldimport=true');
  },

  shouldStartImport() {
    return window.location.search.includes('startImport=true');
  },

  onImportStart() {
    if (!Wahanda.Permissions.editClientContactDetails()) {
      return;
    }

    this._startImport({
      source: 'clients_list_button',
      autoOpened: false,
      clientListEmpty: this.totalCustomers === 0,
    });
  },

  onImportStartFromEmptyState() {
    if (!Wahanda.Permissions.editClientContactDetails()) {
      return;
    }

    this._startImport({
      source: 'clients_list_empty_state',
      autoOpened: false,
      clientListEmpty: this.totalCustomers === 0,
    });
  },

  _startImport(entryContext) {
    if (!Wahanda.Permissions.editClientContactDetails()) {
      return;
    }

    trackEvent('clients-list', 'click', 'add-new-client-from-file');
    if (Wahanda.Features.isEnabled('CCM-1502-redesign-client-list-import')) {
      App.ES6.Initializers.ClientListImportModal({
        source: entryContext.source,
        onUseTemplate: () => {
          App.ES6.Initializers.ClientsImport({
            refreshClientsList: () => this.refreshAfterAdd(),
            entryContext: {
              ...entryContext,
              source: 'clients_list_import_method',
            },
          }).render();
        },
      }).render();
      return;
    }

    if (this.useNewCustomerImportFlow()) {
      const refreshClientsList = () => {
        this.refreshAfterAdd();
      };
      App.ES6.Initializers.ClientsImport({
        refreshClientsList,
        entryContext,
      }).render();
    } else {
      const formDialog = new App.Views.Forms.CustomerImport();
      formDialog.render();
      formDialog.open();
    }
  },

  onMassEmail() {
    trackEvent('clients-list', 'click', 'send-email');

    const editPermissions =
      Wahanda.Permissions.editClientContactDetails() &&
      Wahanda.Permissions.viewClientContactDetails();
    if (!editPermissions) {
      this.disableEmailButton();
    } else {
      const formDialog = new App.Views.Forms.Customer.MassEmail({
        filters: this.collection.filters,
      });
      formDialog.render();
      formDialog.open();
    }
  },

  onBulkDelete() {
    App.ES6.Initializers.ClientsBulkDelete.render({
      onDeleteStart: () => this.$results.loadmask(),
      onDeleteComplete: () => App.trigger(Wahanda.Event.CUSTOMERS_BULK_DELETE),
    });
  },
  bindTooltips() {
    const opts = {
      position: {
        viewport: true,
        my: 'right center',
        at: 'left top',
      },
    };
    this.$el.tooltipize(opts);
  },
});
